import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_DELAY_MS = 300;

export default function useDelayedLoadingIndicator(delayMs = DEFAULT_DELAY_MS) {
  const [showIndicator, setShowIndicator] = useState(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRequestsRef = useRef(0);

  const clearDelayTimer = useCallback(() => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearDelayTimer(), [clearDelayTimer]);

  const stopLoading = useCallback(() => {
    activeRequestsRef.current = Math.max(0, activeRequestsRef.current - 1);
    if (activeRequestsRef.current === 0) {
      clearDelayTimer();
      setShowIndicator(false);
    }
  }, [clearDelayTimer]);

  const startLoading = useCallback(() => {
    activeRequestsRef.current += 1;
    if (activeRequestsRef.current > 1) {
      return;
    }

    clearDelayTimer();
    delayTimerRef.current = setTimeout(() => {
      if (activeRequestsRef.current > 0) {
        setShowIndicator(true);
      }
    }, delayMs);
  }, [clearDelayTimer, delayMs]);

  const runWithLoading = useCallback(
    async <T>(task: () => Promise<T>): Promise<T> => {
      startLoading();
      try {
        return await task();
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  return { showIndicator, runWithLoading };
}
